import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthShell } from '@/components/layouts/auth-shell'
import { GoogleSignInButton } from '@/components/features/auth/google-sign-in-button'
import { useForm, usePage, Link, router } from '@inertiajs/react'
import { FormEvent, useState } from 'react'
import { t } from "@/lib/i18n"

// Kept in step with User::DINER_NAME_MAX_LENGTH. The invite screens read it from
// their props; this page has none of its own, and a shared prop for one number on
// the sign-in screen is not worth the wiring.
const DINER_NAME_MAX_LENGTH = 40

export default function UserSessionsNew() {
    const { flash = {} } = usePage<{
        flash: {
            alert?: string
            notice?: string
        }
    }>().props
    const { data, setData, post } = useForm({
        email_address: '',
        password: '',
    })
    const [guestName, setGuestName] = useState<string | null>(null)
    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        post('/session')
    }
    // A guest is still a name on someone's bill, so the button asks before it
    // starts a session rather than seating an "Invitado a3f2c1".
    const continueAsGuest = () => {
        if (guestName === null) {
            setGuestName('')
            return
        }
        if (guestName.trim().length === 0) return

        router.post('/session/guest', { name: guestName })
    }
    return (<AuthShell title={t("ui.auth.titles.user_sign_in")} headline={t("ui.auth.user.headline")} blurb={t("ui.auth.user.blurb")}>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">{t("ui.react.pages.user.sessions.new.welcome_new")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("ui.react.pages.user.sessions.new.enter_email_below_start_session")}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {flash.alert && (<div role="alert" data-testid="session-flash-alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {flash.alert}
            </div>)}
          <div className="grid gap-2">
            <Label htmlFor="email_address">{t("ui.react.pages.user.sessions.new.email")}</Label>
            <Input id="email_address" type="email" placeholder={t("ui.react.pages.user.sessions.new.m_ejemplo_com")} value={data.email_address} onChange={(e) => setData('email_address', e.target.value)} required/>
          </div>
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="password">{t("ui.react.pages.user.sessions.new.password")}</Label>
              {/* The diner domain had no way back into an account until
                  now. It also serves the people who never had a password:
                  the same link sets a first one. */}
              <Link href="/passwords/new" className="ml-auto text-sm underline underline-offset-4" data-testid="forgot-password-link">
                {t("ui.react.pages.user.sessions.new.forgot_password")}
              </Link>
            </div>
            <Input id="password" type="password" value={data.password} onChange={(e) => setData('password', e.target.value)} required/>
          </div>
          <Button type="submit" className="w-full">{t("ui.react.pages.user.sessions.new.start_session")}</Button>
          <GoogleSignInButton label={t("ui.react.pages.user.sessions.new.continue_with_google")}/>
          {guestName !== null && (<div className="grid gap-2" data-testid="guest-name-field">
              <Label htmlFor="guest_name">{t("ui.react.pages.user.sessions.new.guest_name_label")}</Label>
              {/* This input sits inside the sign-in form, so Enter — the "Go"
                  key on a phone keyboard — would submit the login instead and
                  bounce off the required email field. Enter here means guest. */}
              <Input id="guest_name" value={guestName} onChange={(e) => setGuestName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); continueAsGuest() } }} maxLength={DINER_NAME_MAX_LENGTH} autoComplete="given-name" autoFocus placeholder={t("ui.react.pages.user.sessions.new.guest_name_placeholder")} data-testid="guest-name-input"/>
              <p className="text-xs text-muted-foreground">{t("ui.react.pages.user.sessions.new.guest_name_hint")}</p>
            </div>)}
          <Button type="button" variant="outline" className="w-full" onClick={continueAsGuest} disabled={guestName !== null && guestName.trim().length === 0} data-testid="continue-as-guest">
            {guestName === null
              ? t("ui.react.pages.user.sessions.new.continue_as_guest")
              : t("ui.react.pages.user.sessions.new.guest_continue")}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{t("ui.react.pages.user.sessions.new.flow_pickup_requires_bill_registered")}</p>
          <div className="text-center text-sm">{t("ui.react.pages.user.sessions.new.no_tienes_bill")}{' '}
            <Link href="/registration/new" className="underline underline-offset-4">{t("ui.react.pages.user.sessions.new.register")}</Link>
          </div>
        </div>
      </form>
    </AuthShell>)
}
